/**
 * 🎫 Ticketing — Lista de tickets
 * Ruta: /apps/tickets
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Badge,
  EmptyState,
  Divider,
  Box,
  Spinner,
  IndexTable,
  Pagination,
  Tabs,
  Filters,
} from '@/lib/polaris';
import {
  NoteIcon,
  PlusIcon,
} from '@/lib/polaris-icons';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';

interface Ticket {
  id: string;
  ticket_number: number;
  subject: string;
  status: string;
  priority: string;
  category: string | null;
  source: string;
  assigned_to: string | null;
  contact_id: string | null;
  first_response_due_at: string | null;
  resolution_due_at: string | null;
  first_responded_at: string | null;
  sla_first_response_met: boolean | null;
  sla_resolution_met: boolean | null;
  satisfaction_rating: number | null;
  created_at: string;
  updated_at: string;
}

const STATUS_TONES: Record<string, 'success' | 'warning' | 'critical' | 'info' | 'new'> = {
  new: 'info',
  open: 'warning',
  waiting: 'new',
  resolved: 'success',
  closed: 'success',
};

const STATUS_LABELS: Record<string, string> = {
  new: 'Nuevo',
  open: 'En Progreso',
  waiting: 'Esperando',
  resolved: 'Resuelto',
  closed: 'Cerrado',
};

const PRIORITY_TONES: Record<string, 'success' | 'warning' | 'critical' | 'info'> = {
  low: 'info',
  medium: 'warning',
  high: 'critical',
  urgent: 'critical',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  urgent: 'Urgente',
};

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  email: 'Email',
  whatsapp: 'WhatsApp',
  web_form: 'Formulario',
  phone: 'Teléfono',
  api: 'API',
};

const TAB_FILTERS = [
  { id: 'all', content: 'Todos' },
  { id: 'open', content: 'Abiertos' },
  { id: 'my', content: 'Mis tickets' },
  { id: 'unassigned', content: 'Sin asignar' },
  { id: 'urgent', content: 'Urgentes' },
  { id: 'sla_breach', content: 'SLA excedido' },
];

export default function TicketsListPage() {
  const storeNavigate = useStoreNavigate();
  const { tenantId, user } = useAuth();
  // AuthContextValue NO expone `userId` — el id del usuario vive en `user.id`. Leerlo como
  // `userId` daba SIEMPRE undefined, y los guards `if (!userId) return` mataban la feature entera.
  const userId = user?.id;
  const toast = useAdminToast();

  const [page, setPage] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 25;

  // Create dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tSubject, setTSubject] = useState('');
  const [tDesc, setTDesc] = useState('');
  const [tPriority, setTPriority] = useState('medium');
  const [tCategory, setTCategory] = useState('');
  const [saving, setSaving] = useState(false);

  // Por la caché (ADR-0012, paso 3). La clave lleva TODO lo que cambia el resultado —pestaña,
  // búsqueda, página—, que es lo que hace que volver a la misma vista no pida nada.
  const qc = useQueryClient();

  const consulta = useQuery({
    queryKey: ['tickets', tenantId, userId, selectedTab, searchQuery.trim(), page],
    enabled: !!tenantId,
    // 🪤 Sin esto la tabla se vacía en cada tecleo del buscador y en cada cambio de página: se ve
    // como un parpadeo. Con esto, las filas viejas se quedan hasta que llegan las nuevas.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async (): Promise<{ tickets: Ticket[]; total: number }> => {
      let query = supabase
        .from('tickets')
        .select('*', { count: 'exact' })
        .eq('tenant_id', tenantId!);

      // Tab filters
      const tabId = TAB_FILTERS[selectedTab].id;
      if (tabId === 'open') {
        query = query.in('status', ['new', 'open', 'waiting']);
      } else if (tabId === 'my' && userId) {
        query = query.eq('assigned_to', userId);
      } else if (tabId === 'unassigned') {
        query = query.is('assigned_to', null).in('status', ['new', 'open']);
      } else if (tabId === 'urgent') {
        query = query.eq('priority', 'urgent').in('status', ['new', 'open', 'waiting']);
      } else if (tabId === 'sla_breach') {
        query = query.or('sla_first_response_met.eq.false,sla_resolution_met.eq.false');
      }

      if (searchQuery.trim()) {
        query = query.ilike('subject', `%${searchQuery.trim()}%`);
      }

      const offset = (page - 1) * pageSize;
      query = query.order('created_at', { ascending: false }).range(offset, offset + pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;
      return { tickets: (data ?? []) as Ticket[], total: count ?? 0 };
    },
  });

  const tickets = useMemo(() => consulta.data?.tickets ?? [], [consulta.data]);
  const total = consulta.data?.total ?? 0;
  // 🪤 `isPending`, no `isFetching`: con `keepPreviousData`, al cambiar de página ya hay datos
  // previos y `isPending` es false — que es justo lo que evita el parpadeo.
  const loading = !!tenantId && consulta.isPending;

  useEffect(() => {
    if (consulta.error) toast.error('Error al cargar tickets', getErrorMessage(consulta.error));
  }, [consulta.error, toast]);


  const handleCreate = async () => {
    if (!tenantId || !tSubject.trim()) return;
    setSaving(true);

    try {
      const { data, error } = await supabase
        .from('tickets')
        .insert({
          tenant_id: tenantId,
          subject: tSubject.trim(),
          description: tDesc.trim() || null,
          priority: tPriority,
          category: tCategory.trim() || null,
          created_by: userId,
        })
        .select('id')
        .single();

      if (error) throw error;
      toast.success('Ticket creado');
      setDialogOpen(false);
      // 🪤 Crear un ticket navega al detalle, así que esta lista no se vuelve a montar y nadie la
      // recarga. Sin invalidar, al volver se vería la lista SIN el ticket recién creado hasta que
      // la caché venciera sola — un "no se guardó" que no ocurrió. Es el precio de cachear, y se
      // paga avisando en la mutación.
      void qc.invalidateQueries({ queryKey: ['tickets', tenantId] });
      if (data) storeNavigate(`/apps/tickets/${data.id}`);
    } catch (error: unknown) {
      toast.error('Error al crear ticket', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const isSlaBreached = (ticket: Ticket): boolean => {
    if (ticket.status === 'resolved' || ticket.status === 'closed') return false;
    const now = new Date();
    if (ticket.first_response_due_at && !ticket.first_responded_at && new Date(ticket.first_response_due_at) < now) return true;
    if (ticket.resolution_due_at && new Date(ticket.resolution_due_at) < now) return true;
    return false;
  };

  return (
    <Page
      title="Tickets"
      subtitle="Service Desk — Soporte al cliente"
      primaryAction={{ content: 'Crear ticket', icon: PlusIcon, onAction: () => { setTSubject(''); setTDesc(''); setTPriority('medium'); setTCategory(''); setDialogOpen(true); } }}
    >
      <BlockStack gap="400">
        <Tabs tabs={TAB_FILTERS} selected={selectedTab} onSelect={(i) => { setSelectedTab(i); setPage(1); }} />

        <Filters
          queryValue={searchQuery}
          queryPlaceholder="Buscar por asunto..."
          onQueryChange={(v) => { setSearchQuery(v); setPage(1); }}
          onQueryClear={() => setSearchQuery('')}
          filters={[]}
          onClearAll={() => setSearchQuery('')}
        />

        {loading ? (
          <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
        ) : tickets.length === 0 ? (
          <Card>
            <EmptyState heading="Sin tickets" image="" action={{ content: 'Crear ticket', onAction: () => setDialogOpen(true) }}>
              <Text as="p">No hay tickets que coincidan con los filtros seleccionados.</Text>
            </EmptyState>
          </Card>
        ) : (
          <Card padding="0">
            <IndexTable
              headings={[
                { title: '#' },
                { title: 'Asunto' },
                { title: 'Estado' },
                { title: 'Prioridad' },
                { title: 'Origen' },
                { title: 'SLA' },
                { title: 'Creado' },
              ]}
              itemCount={tickets.length}
              selectable={false}
            >
              {tickets.map((ticket, idx) => {
                const breached = isSlaBreached(ticket);
                return (
                  <IndexTable.Row
                    id={ticket.id}
                    key={ticket.id}
                    position={idx}
                    onClick={() => storeNavigate(`/apps/tickets/${ticket.id}`)}
                  >
                    <IndexTable.Cell>
                      <Text as="span" tone="subdued" variant="bodySm">#{ticket.ticket_number}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" fontWeight="medium">{ticket.subject}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={STATUS_TONES[ticket.status] ?? 'new'}>
                        {STATUS_LABELS[ticket.status] ?? ticket.status}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={PRIORITY_TONES[ticket.priority] ?? 'info'}>
                        {PRIORITY_LABELS[ticket.priority] ?? ticket.priority}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" variant="bodySm">{SOURCE_LABELS[ticket.source] ?? ticket.source}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {breached ? (
                        <Badge tone="critical">Excedido</Badge>
                      ) : ticket.sla_first_response_met === false ? (
                        <Badge tone="critical">Respuesta tardía</Badge>
                      ) : ticket.sla_first_response_met ? (
                        <Badge tone="success">OK</Badge>
                      ) : (
                        <Text as="span" tone="subdued" variant="bodySm">—</Text>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text as="span" variant="bodySm">{formatDate(ticket.created_at)}</Text>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          </Card>
        )}

        {total > pageSize && (
          <InlineStack align="center">
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage(page - 1)}
              hasNext={page * pageSize < total}
              onNext={() => setPage(page + 1)}
            />
          </InlineStack>
        )}
      </BlockStack>

      {/* Modal Crear */}
      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nuevo ticket"
        primaryAction={{ content: 'Crear', onAction: handleCreate, loading: saving, disabled: !tSubject.trim() }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDialogOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField label="Asunto" value={tSubject} onChange={setTSubject} placeholder="Ej: Problema con mi pedido #1234" autoComplete="off" requiredIndicator />
            <TextField label="Descripción" value={tDesc} onChange={setTDesc} placeholder="Detalle del problema..." multiline={4} autoComplete="off" />
            <Select
              label="Prioridad"
              options={[
                { label: 'Baja', value: 'low' },
                { label: 'Media', value: 'medium' },
                { label: 'Alta', value: 'high' },
                { label: 'Urgente', value: 'urgent' },
              ]}
              value={tPriority}
              onChange={setTPriority}
            />
            <TextField label="Categoría" value={tCategory} onChange={setTCategory} placeholder="Ej: Envío, Pago, Producto" autoComplete="off" />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
